import dayjs from "dayjs";
import { message } from "antd";

const BASE_URL = "/api";
const HEADERS_JSON = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const getToken = (): string | null => {
  return localStorage.getItem("accessToken");
};

const request = async <T>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    path: string,
    body?: any,
    isJson = true,
    responseType: "json" | "blob" = "json"
): Promise<T> => {
  const token = getToken();
  const url = `${BASE_URL}${path}`;

  try {
    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...(method !== "GET" && { Timestamp: dayjs().format("YYYY-MM-DDTHH:mm:ss") }),
        ...(isJson && { "Content-Type": "application/json" }),
      },
      credentials: "include",
      ...(body ? { body: isJson ? JSON.stringify(body) : body } : {}),
    });

    const data = await handleResponse(response, responseType);
    return data as T;
  } catch (error: unknown) {
    const err = error as { errorData?: { message?: string } };
    if (err?.errorData?.message === "TokenExpiredError") {
      await refreshAccessToken();
      return post<T>(path, body, isJson, responseType);
    }
    if (err?.errorData) {
      throw new Error(err.errorData?.message ?? "Error occurred during POST request");
    }
    throw new Error("Error occurred during POST request");
  }
};

export const get = <T>(path: string, responseType: "json" | "blob" = "json") =>
    request<T>("GET", path, undefined, true, responseType);
export const post = <T>(path: string, body: any, isJson = true, responseType: "json" | "blob" = "json") =>
    request<T>("POST", path, body, isJson, responseType);
export const put = <T>(path: string, body: any, isJson = true, responseType: "json" | "blob" = "json") =>
    request<T>("PUT", path, body, isJson, responseType);
export const del = <T>(path: string, body?: any) =>
    request<T>("DELETE", path, body);
export const patch = <T>(path: string, body: any, isJson = true, responseType: "json" | "blob" = "json") =>
    request<T>("PATCH", path, body, isJson, responseType);

const handleResponse = async (response: Response, responseType: string) => {
  if (response.ok) {
    return responseType === "blob" ? response.blob() : response.json();
  } else {
    const errorData = await response.json();
    throw { status: response.status, errorData };
  }
};

interface RefreshTokenResponse {
  accessToken: string | undefined;
}
let refreshInProgress = false;
let pendingRequests: any[] = [];

const REFRESH_FAILED_MESSAGE = "Не удалось обновить токен. Перезайдите в систему.";

export const refreshAccessToken = async (): Promise<boolean> => {
  if (refreshInProgress) {
    message.error("Токен обновляется. Подождите...");
    return new Promise((resolve) => {
      pendingRequests.push(resolve);
    });
  }

  refreshInProgress = true;

  try {
    const response = await post<RefreshTokenResponse>("/auth/refresh-token", {});

    if (response?.accessToken) {
      localStorage.setItem("accessToken", response.accessToken);
      pendingRequests.forEach((resolve) => resolve(true));
      pendingRequests = [];
      return true;
    }
    localStorage.removeItem("accessToken");
    window.location.replace("/login");
    throw new Error(REFRESH_FAILED_MESSAGE);
  } catch (error) {
    console.error("Error refreshing token:", error);
    pendingRequests.forEach((resolve) => resolve(false));
    pendingRequests = [];
    if (error instanceof Error && error.message === REFRESH_FAILED_MESSAGE) {
      throw error;
    }
    throw new Error(REFRESH_FAILED_MESSAGE);
  } finally {
    refreshInProgress = false;
  }
};

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

export const download = async (path: string, defaultFilename?: string): Promise<DownloadResult> => {
  const makeRequest = async (): Promise<Response> => {
    const token = getToken();
    const authHeader = `Bearer ${token}`;

    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream',
        Authorization: authHeader,
      },
      credentials: 'include',
    });

    if (response.status === 401) {
      const error = await response.clone().json();
      if (error?.message === "TokenExpiredError") {
        await refreshAccessToken();
        const newToken = getToken();
        return fetch(`${BASE_URL}${path}`, {
          method: "GET",
          headers: {
            Accept: "application/octet-stream",
            Authorization: `Bearer ${newToken}`,
          },
          credentials: "include",
        });
      }
      throw new Error("Не авторизован. Перезайдите в систему.");
    }

    if (!response.ok) {
      let errorMessage = 'Ошибка при скачивании файла';
      try {
        const error = await response.clone().json();
        errorMessage = error?.error ?? error?.message ?? errorMessage;
      } catch (e) {
        throw e;
      }
      throw new Error(errorMessage);
    }

    return response;
  };

  const response = await makeRequest();
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
  let filename = defaultFilename || 'download';

  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
    if (fileNameMatch && fileNameMatch[1]) {
      filename = decodeURIComponent(fileNameMatch[1]);
    }
  }

  return { blob, filename };
};